import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import { DataTable } from "../../../../ui-components/data-table";
import LabeledTextInput from "../../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSSelect, { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import { DenimUploadBatch, DenimUploadBatchStatus } from "../../../../app/data/invoice-factoring/models";
import {
  ACTIVE_BATCH_STATUSES,
  BATCH_STATUS_LABELS,
  FACTORING_FILTER_DEBOUNCE_MS,
  FACTORING_PAGE_SIZE,
  FACTORING_POLL_MS,
  FACTORING_QUERY_OPTIONS,
} from "../constants";
import { useFactoringContext } from "../common/useFactoringContext";
import FactoringNav from "../common/factoringNav";
import BatchesSummaryTiles from "./batchesSummaryTiles";
import NewUploadSidebar from "./newUploadSidebar";
import getBatchesColumns from "./batchesColumns";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const statusOptions: XGSSelectOption[] = [
  { value: "", label: "All statuses" },
  ...(Object.keys(BATCH_STATUS_LABELS) as DenimUploadBatchStatus[]).map((status) => ({
    value: status,
    label: BATCH_STATUS_LABELS[status],
  })),
];

const selectMenuStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const FactoringBatches: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { canManage, terminalPrefix } = useFactoringContext();

  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [searchInput, setSearchInput] = useState(() => initialParams.get("search") || "");
  const [search, setSearch] = useState(() => initialParams.get("search") || "");
  const [status, setStatus] = useState<DenimUploadBatchStatus | "">(
    () => (initialParams.get("status") as DenimUploadBatchStatus) || ""
  );
  const [showUpload, setShowUpload] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(debounce(setSearch, FACTORING_FILTER_DEBOUNCE_MS), []);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_batches" });
  }, []);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status) q.set("status", status);
    const qs = q.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: [QUERY_KEYS.INVOICE_FACTORING_STATS],
    queryFn: () => invoiceFactoringService.getStats(),
    retry: false,
    refetchInterval: (data) => (data?.active ? FACTORING_POLL_MS : false),
    ...FACTORING_QUERY_OPTIONS,
  });

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPreviousData,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.INVOICE_FACTORING_BATCHES, search, status],
    queryFn: ({ pageParam = 1 }) =>
      invoiceFactoringService.getBatches({
        page: pageParam,
        pageSize: FACTORING_PAGE_SIZE,
        search: search || undefined,
        status: status || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.pagination?.next ?? undefined,
    keepPreviousData: true,
    retry: false,
    refetchInterval: (current) =>
      current?.pages.some((page) => page.items.some((batch) => ACTIVE_BATCH_STATUSES.includes(batch.status)))
        ? FACTORING_POLL_MS
        : false,
    ...FACTORING_QUERY_OPTIONS,
  });

  const batches = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const hasNextPage = !!data?.pages[data.pages.length - 1]?.pagination?.next;
  const total = data?.pages[0]?.pagination?.total;
  const columns = useMemo(() => getBatchesColumns(), []);

  const openBatch = (batchId: string) =>
    history.push(`${terminalPrefix}${Routes.billing.invoiceFactoringBatches}/${batchId}`);

  const onClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
  };

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_BATCHES}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.INVOICE_FACTORING_BATCHES}
          title="Invoice Factoring"
          mods={{ "full-width": true }}
        >
          {canManage && (
            <Button theme={ButtonThemes.blue} size={ButtonSizes.auto} type="button" onClick={() => setShowUpload(true)}>
              New upload
            </Button>
          )}
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-invoice-factoring">
        <FactoringNav />

        <BatchesSummaryTiles
          stats={stats}
          isLoading={statsLoading}
          onStatusFilter={setStatus}
          onOpenInvoicesNeedingAttention={() =>
            history.push(`${terminalPrefix}${Routes.billing.invoiceFactoringInvoices}?status=NEEDS_ATTENTION`)
          }
        />

        <div className="xgs-invoice-factoring__filters">
          <LabeledTextInput
            label="Search:"
            labelMode={LabelModes.column}
            type="text"
            value={searchInput}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            placeholder="Batch number, name, file, uploader, or probill"
            className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-search"
          />
          <div className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-select">
            <span className="xgs-invoice-factoring__filter-label">Status:</span>
            <XGSSelect
              value={statusOptions.find((o) => o.value === status) ?? statusOptions[0]}
              options={statusOptions}
              onValueChange={(o) => setStatus((o?.value as DenimUploadBatchStatus) || "")}
              isClearable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuStyles}
            />
          </div>
          {(search || status) && (
            <Button theme={ButtonThemes.blueInverted} type="button" onClick={onClearFilters} className="xgs-invoice-factoring__filter-clear">
              Clear filters
            </Button>
          )}
          {total !== undefined && <span className="xgs-invoice-factoring__muted">{total} batches</span>}
        </div>

        {isError && (
          <div className="xgs-invoice-factoring__error-box">
            Unable to load batches. {(error as Error)?.message}
          </div>
        )}

        <DataTable
          columns={columns}
          data={batches}
          isLoading={isLoading || (isFetching && isPreviousData)}
          keepTableOnLoading
          cursorPointer
          rowHeight={56}
          noResultsText={
            canManage ? "No batches found. Adjust filters or upload a CSV to create one." : "No batches found. Adjust filters."
          }
          infiniteScroll
          infiniteScrollLoading={isFetchingNextPage}
          infiniteScrollHasNext={hasNextPage}
          onInfiniteScroll={() => fetchNextPage()}
          onRowClicked={(row: DenimUploadBatch) => openBatch(row.id)}
          stickyHeaders
          responsive
        />
      </div>

      {canManage && (
        <NewUploadSidebar
          show={showUpload}
          onClose={() => setShowUpload(false)}
          onCreated={() => {
            queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_BATCHES]);
            queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_STATS]);
          }}
          onOpenBatch={openBatch}
        />
      )}
    </ContentContainer>
  );
};

export default FactoringBatches;
